// Path: src/pages/UserLessonsPage.tsx
// Author: ISTP_Dev
// Description: Main container for Permission Management.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getSchools, getPermissionCourses, type School } from '../lib/api';
import ResourceSidebar from '../components/permissions/ResourceSidebar';
import PermissionTable from '../components/permissions/PermissionTable';
import EntityInspector from '../components/permissions/EntityInspector';
import PermissionModal from '../components/permissions/PermissionModal';
// ✅ 导入共享类型
import { type CourseNode, type ResourceContext } from '../types/permissionTypes';

export interface StageNode {
  id: number;
  title: string;
  sort_order?: number;
  lessons?: LessonNode[];
}
export interface LessonNode {
  id: number;
  title: string;
  sort_order?: number;
}

const UserLessonsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'resource' | 'entity'>('resource');
  const [schools, setSchools] = useState<School[]>([]);
  const [courses, setCourses] = useState<CourseNode[]>([]);
  
  // Resource View State
  const [selectedNode, setSelectedNode] = useState<ResourceContext | null>(null);
  const [drillDownSchool, setDrillDownSchool] = useState<School | null>(null);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPerm, setEditingPerm] = useState<any>(null); // Passed to modal
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Simple counter to trigger refetch

  // Load Initial Data
  useEffect(() => {
    const init = async () => {
      try {
        const [s, c] = await Promise.all([getSchools({ limit: 1000 }), getPermissionCourses()]);
        setSchools(s.data);
        setCourses(c.data);
      } catch (e) { console.error("Init failed", e); }
    };
    init();
  }, []);

  const handleRefresh = () => setRefreshTrigger(prev => prev + 1);

  return (
    <div className="min-h-screen bg-gray-50 p-6 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header & Tabs */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {/* 返回按钮 */}
            <Link 
              to="/admin" 
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
              title="返回管理控制台"
            >
              <ArrowLeft size={16} />
              <span className="text-sm">返回</span>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">用户课程权限管理</h1>
          </div>
          <div className="flex bg-white rounded-lg p-1 shadow-sm border">
            <TabButton label="按资源管理" active={activeTab === 'resource'} onClick={() => setActiveTab('resource')} />
            <TabButton label="按实体反查" active={activeTab === 'entity'} onClick={() => setActiveTab('entity')} />
          </div>
        </div>

        {/* Content Area */}
        {activeTab === 'resource' ? (
          <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-150px)]">
            <div className="w-full lg:w-1/3 bg-white rounded-xl shadow-sm border border-gray-200 overflow-y-auto">
              <ResourceSidebar 
                courses={courses} 
                selectedNode={selectedNode} 
                onSelect={(node) => {
                  setSelectedNode(node);
                  setDrillDownSchool(null); // Reset drilldown on new selection
                }} 
              />
            </div>
            <div className="w-full lg:w-2/3 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
              <PermissionTable 
                selectedNode={selectedNode}
                drillDownSchool={drillDownSchool}
                onDrillDown={setDrillDownSchool}
                onAdd={() => { setEditingPerm(null); setIsModalOpen(true); }}
                onEdit={(perm) => { setEditingPerm(perm); setIsModalOpen(true); }}
                refreshTrigger={refreshTrigger}
              />
            </div>
          </div>
        ) : (
          <EntityInspector schools={schools} />
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <PermissionModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); handleRefresh(); }}
          schools={schools}
          selectedNode={selectedNode}
          drillDownSchool={drillDownSchool}
          editingData={editingPerm}
        />
      )}
    </div>
  );
};

const TabButton = ({ label, active, onClick }: { label: string, active: boolean, onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
      active ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
    }`}
  >
    {label}
  </button>
);

export default UserLessonsPage;
